// @flow

import React, { Component } from 'react';
import styled from 'styled-components';
import { Button, InputGroup } from '@blueprintjs/core';

import SettingsPageHeader from '../settings-page-header';
import { Wrapper, TablesBody, TablesWrapper } from '../settings-page-styles';
import Layout from '../../../layouts/default-logged-in';
import ActiveUsersTable from '../../../settings-tables/active-users';
import InvitedUsersTable from '../../../settings-tables/invited-users';
import InviteUserMenu from '../../../invite-user-menu';

import PreferencesFilterBar, {
  userPreferenceFilterPredicate,
} from '../../../user-profile/preferences-filter-adapter';
import type { ActivePreferenceFilter } from '../../../user-profile/preferences-filter-adapter';
import {
  PreferencesBulkBar,
  PreferencesBulkDialog,
} from '../../../user-profile/preferences-bulk-adapter';
import PillErrorBoundary from '../../../pills/pill-error-boundary';
import type { UserProfilePermissionsT } from '../../../user-profile/use-user-profile-permissions';

import { trackEvent, ENTER_PAGE } from '../../../../services/mixpanel';

import type { BrowserHistory } from 'history/createBrowserHistory';
import * as UserModel from '../../../../models/user';
import * as LocationModel from '../../../../models/location';
import * as UserListModel from '../../../../models/user-list';
import * as RoleModel from '../../../../models/role';

// Toolbar styled to match the locations page's PageHeaderRow (no white
// box around the controls — sits directly on the page background).
const Toolbar = styled.div`
  flex: 0 0 auto;
  background: var(--main-background, transparent);
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
`;

const Subtitle = styled.span`
  color: #5c7080;
  font-size: 13px;
`;

const HeaderSpacer = styled.span`
  flex: 1;
`;

const SearchBox = styled(InputGroup)`
  width: 240px;
`;

// One section per table: the contextual bulk bar (when a selection exists
// in that table) sits directly above its own TablesWrapper. Margin
// separates the two sections so the active and invited panels read as
// distinct surfaces.
const TableSection = styled.div`
  & + & {
    margin-top: 24px;
  }
`;

type LoadingFlags = {
  normal: boolean,
  invited: boolean,
  adopted: boolean,
};

type ErrorFlags = {
  normal: ?string,
  invited: ?string,
  adopted: ?string,
};

// Per user population: whether that list came back with its embedded role
// rows. Rows from a population without them can't offer role editing.
type RolesIncludeFlags = {
  normal: boolean,
  invited: boolean,
  adopted: boolean,
};

type BulkDialogState = {
  mode: 'add' | 'remove',
  table: 'active' | 'invited',
};

type Props = {
  user: UserModel.t,
  locations: LocationModel.t[],
  history: BrowserHistory,
  activeUsers: UserListModel.userT[],
  invitedUsers: UserListModel.userT[],
  loading: LoadingFlags,
  errors: ErrorFlags,
  rolesIncludeLoaded: RolesIncludeFlags,
  rolesCatalogue: RoleModel.RolesCatalogueT,
  fetchAllUsers: (options?: {
    include?: Array<'preferences' | 'roles'>,
  }) => Promise<void>,
  fetchTags: () => Promise<void>,
  addRole: (userId: number, role: RoleModel.catalogueRoleT) => Promise<void>,
  removeRole: (userId: number, roleId: number) => Promise<void>,
  removeUser: (
    userId: number,
    userType: UserListModel.userListTypeEnumT,
  ) => Promise<void>,
  permissions: UserProfilePermissionsT,
};

type State = {
  pageLoadError: boolean,
  isInviteMenuOpen: boolean,
  preferenceFilters: $ReadOnlyArray<ActivePreferenceFilter>,
  emailSearch: string,
  selectedActiveIds: Set<number>,
  selectedInvitedIds: Set<number>,
  bulkDialog: ?BulkDialogState,
};

// Builds the combined per-row predicate (preference filters AND email
// substring) used by both tables. Exported as a closure so the page can
// reuse it for selection-bookkeeping and bulk-bar counts without
// re-deriving it inside JSX.
const buildCombinedPredicate = (
  filters: $ReadOnlyArray<ActivePreferenceFilter>,
  emailSearchRaw: string,
): ((user: UserListModel.userT) => boolean) => {
  const prefPredicate = userPreferenceFilterPredicate(filters);
  const term = emailSearchRaw.trim().toLowerCase();
  if (term === '') return prefPredicate;
  return (user) => {
    if (!prefPredicate(user)) return false;
    const email = (user && user.email) || '';
    return email.toLowerCase().includes(term);
  };
};

class SettingsUsers extends Component<Props, State> {
  state = {
    pageLoadError: false,
    isInviteMenuOpen: false,
    preferenceFilters: [],
    emailSearch: '',
    selectedActiveIds: new Set(),
    selectedInvitedIds: new Set(),
    bulkDialog: null,
  };

  componentDidMount() {
    trackEvent(ENTER_PAGE, 'SETTINGS_USERS');
    this.props.fetchAllUsers();
    this.props.fetchTags();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const filtersChanged =
      prevState.preferenceFilters !== this.state.preferenceFilters;
    const searchChanged = prevState.emailSearch !== this.state.emailSearch;
    if (filtersChanged || searchChanged) {
      const predicate = buildCombinedPredicate(
        this.state.preferenceFilters,
        this.state.emailSearch,
      );
      const activeVisible = new Set(
        this.props.activeUsers.filter(predicate).map((u) => u.id),
      );
      const invitedVisible = new Set(
        this.props.invitedUsers.filter(predicate).map((u) => u.id),
      );
      this.setState((s) => ({
        selectedActiveIds: new Set(
          [...s.selectedActiveIds].filter((id) => activeVisible.has(id)),
        ),
        selectedInvitedIds: new Set(
          [...s.selectedInvitedIds].filter((id) => invitedVisible.has(id)),
        ),
      }));
    }
  }

  setIsInviteMenuOpen(value: boolean) {
    this.setState({
      isInviteMenuOpen: value,
    });
  }

  setEmailSearch = (value: string) => {
    this.setState({ emailSearch: value });
  };

  clearEmailSearch = () => {
    this.setState({ emailSearch: '' });
  };

  addFilter = (filter: ActivePreferenceFilter) => {
    this.setState((s) => {
      const exists = s.preferenceFilters.find(
        (f) => f.id === filter.id && f.categoryKey === filter.categoryKey,
      );
      if (exists) return null;
      return { preferenceFilters: [...s.preferenceFilters, filter] };
    });
  };

  removeFilter = (filter: ActivePreferenceFilter) => {
    this.setState((s) => ({
      preferenceFilters: s.preferenceFilters.filter(
        (f) => !(f.id === filter.id && f.categoryKey === filter.categoryKey),
      ),
    }));
  };

  clearFilters = () => {
    this.setState({ preferenceFilters: [] });
  };

  toggleActiveRow = (id: number) => {
    this.setState((s) => {
      const next = new Set(s.selectedActiveIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedActiveIds: next };
    });
  };

  toggleAllActive = (visibleIds: number[]) => {
    this.setState((s) => {
      const allSelected = visibleIds.every((id) => s.selectedActiveIds.has(id));
      const next = new Set(s.selectedActiveIds);
      visibleIds.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return { selectedActiveIds: next };
    });
  };

  clearActiveSelection = () => {
    this.setState({ selectedActiveIds: new Set() });
  };

  selectAllActiveVisible = () => {
    const predicate = buildCombinedPredicate(
      this.state.preferenceFilters,
      this.state.emailSearch,
    );
    const ids = this.props.activeUsers
      .filter(predicate)
      .map((u) => u.id);
    this.setState({ selectedActiveIds: new Set(ids) });
  };

  toggleInvitedRow = (id: number) => {
    this.setState((s) => {
      const next = new Set(s.selectedInvitedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedInvitedIds: next };
    });
  };

  toggleAllInvited = (visibleIds: number[]) => {
    this.setState((s) => {
      const allSelected = visibleIds.every((id) =>
        s.selectedInvitedIds.has(id),
      );
      const next = new Set(s.selectedInvitedIds);
      visibleIds.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return { selectedInvitedIds: next };
    });
  };

  clearInvitedSelection = () => {
    this.setState({ selectedInvitedIds: new Set() });
  };

  selectAllInvitedVisible = () => {
    const predicate = buildCombinedPredicate(
      this.state.preferenceFilters,
      this.state.emailSearch,
    );
    const ids = this.props.invitedUsers
      .filter(predicate)
      .map((u) => u.id);
    this.setState({ selectedInvitedIds: new Set(ids) });
  };

  openBulk = (table: 'active' | 'invited', mode: 'add' | 'remove') => {
    this.setState({ bulkDialog: { table, mode } });
  };

  closeBulk = () => {
    this.setState({ bulkDialog: null });
  };

  completeBulk = () => {
    const dialog = this.state.bulkDialog;
    if (dialog && dialog.table === 'active') this.clearActiveSelection();
    else if (dialog && dialog.table === 'invited')
      this.clearInvitedSelection();
    this.setState({ bulkDialog: null });
  };

  render() {
    const {
      pageLoadError,
      isInviteMenuOpen,
      preferenceFilters,
      emailSearch,
      selectedActiveIds,
      selectedInvitedIds,
      bulkDialog,
    } = this.state;
    const {
      history,
      user,
      activeUsers,
      invitedUsers,
      loading,
      errors,
      rolesIncludeLoaded,
      rolesCatalogue,
      addRole,
      removeRole,
      removeUser,
      fetchAllUsers,
      permissions,
    } = this.props;

    const isLoading = loading.normal || loading.invited || loading.adopted;

    // ActiveTable shows normal + adopted; deny only when BOTH 403, so that
    // a customer admin lacking just `adoptedUser:read` still sees their owned
    // users instead of an empty "no permission" panel.
    const activeTableDenied =
      errors.normal === 'no-permission' && errors.adopted === 'no-permission';

    const inviteHidden =
      errors.normal === 'no-permission' || errors.invited === 'no-permission';

    const allDenied =
      errors.normal === 'no-permission' &&
      errors.invited === 'no-permission' &&
      errors.adopted === 'no-permission';

    const predicate = buildCombinedPredicate(preferenceFilters, emailSearch);

    const bulkCapsResolved =
      permissions && !permissions.loading &&
      (permissions.canBulkAdd || permissions.canBulkRemove);

    const activeFilteredTotal = activeUsers.filter(predicate).length;
    const invitedFilteredTotal = invitedUsers.filter(predicate).length;

    const selectedActiveUsers = activeUsers.filter((u) =>
      selectedActiveIds.has(u.id),
    );
    const selectedInvitedUsers = invitedUsers.filter((u) =>
      selectedInvitedIds.has(u.id),
    );

    const totalUserCount = activeUsers.length + invitedUsers.length;
    const userCountSubtitle = isLoading
      ? 'Loading users…'
      : `${totalUserCount} user${totalUserCount === 1 ? '' : 's'}` +
        (invitedUsers.length > 0
          ? ` · ${invitedUsers.length} invited`
          : '');

    return (
      <Layout>
        <SettingsPageHeader history={history} />
        {(pageLoadError || allDenied) && (
          <Wrapper>
            <TablesBody>
              Could not load this page. You may not have the correct permissions
            </TablesBody>
          </Wrapper>
        )}
        {!pageLoadError && !allDenied && (
          <Wrapper>
            <Toolbar>
              <PageHeaderRow>
                <Title>Manage users</Title>
                <Subtitle>{userCountSubtitle}</Subtitle>
                <HeaderSpacer />
                <SearchBox
                  leftIcon="search"
                  placeholder="Search by email…"
                  value={emailSearch}
                  onChange={(e) => this.setEmailSearch(e.target.value)}
                  rightElement={
                    emailSearch ? (
                      <Button
                        minimal
                        icon="cross"
                        onClick={this.clearEmailSearch}
                        title="Clear search"
                      />
                    ) : undefined
                  }
                />
                {!inviteHidden && (
                  <Button
                    icon="plus"
                    intent="primary"
                    text="Invite New User"
                    disabled={rolesCatalogue.status !== 'ready'}
                    onClick={() => this.setIsInviteMenuOpen(true)}
                  />
                )}
              </PageHeaderRow>

              <PillErrorBoundary>
                <PreferencesFilterBar
                  activeFilters={preferenceFilters}
                  onAdd={this.addFilter}
                  onRemove={this.removeFilter}
                  onClear={this.clearFilters}
                />
              </PillErrorBoundary>
            </Toolbar>

            <TableSection>
              {bulkCapsResolved && selectedActiveIds.size > 0 && (
                <PillErrorBoundary>
                  <PreferencesBulkBar
                    count={selectedActiveIds.size}
                    total={activeFilteredTotal}
                    permissions={permissions}
                    onAdd={() => this.openBulk('active', 'add')}
                    onRemove={() => this.openBulk('active', 'remove')}
                    onClear={this.clearActiveSelection}
                    onSelectAll={this.selectAllActiveVisible}
                  />
                </PillErrorBoundary>
              )}
              <TablesWrapper>
                <TablesBody>
                  <ActiveUsersTable
                    loading={isLoading}
                    users={activeUsers}
                    currentUser={user}
                    deleteUser={(userId, userType) =>
                      removeUser(userId, userType)
                    }
                    addRole={(u, role) => addRole(u.id, role)}
                    removeRole={(u, roleId) => removeRole(u.id, roleId)}
                    rolesCatalogue={rolesCatalogue}
                    rolesIncludeLoaded={rolesIncludeLoaded}
                    permission={!activeTableDenied}
                    selectable={bulkCapsResolved}
                    selectedIds={selectedActiveIds}
                    onToggleRow={this.toggleActiveRow}
                    onToggleAll={this.toggleAllActive}
                    filterPredicate={predicate}
                    permissions={permissions}
                  />
                </TablesBody>
              </TablesWrapper>
            </TableSection>

            {!isLoading && invitedUsers.length !== 0 && (
              <TableSection>
                {bulkCapsResolved && selectedInvitedIds.size > 0 && (
                  <PillErrorBoundary>
                    <PreferencesBulkBar
                      count={selectedInvitedIds.size}
                      total={invitedFilteredTotal}
                      permissions={permissions}
                      onAdd={() => this.openBulk('invited', 'add')}
                      onRemove={() => this.openBulk('invited', 'remove')}
                      onClear={this.clearInvitedSelection}
                      onSelectAll={this.selectAllInvitedVisible}
                    />
                  </PillErrorBoundary>
                )}
                <TablesWrapper>
                  <TablesBody>
                    <InvitedUsersTable
                      loading={isLoading}
                      users={invitedUsers}
                      currentUser={user}
                      deleteUser={(userId, userType) =>
                        removeUser(userId, userType)
                      }
                      addRole={(u, role) => addRole(u.id, role)}
                      removeRole={(u, roleId) => removeRole(u.id, roleId)}
                      rolesCatalogue={rolesCatalogue}
                      rolesIncludeLoaded={rolesIncludeLoaded}
                      selectable={bulkCapsResolved}
                      selectedIds={selectedInvitedIds}
                      onToggleRow={this.toggleInvitedRow}
                      onToggleAll={this.toggleAllInvited}
                      filterPredicate={predicate}
                      permissions={permissions}
                    />
                  </TablesBody>
                </TablesWrapper>
              </TableSection>
            )}

            {bulkDialog && (
              <PillErrorBoundary>
                <PreferencesBulkDialog
                  isOpen
                  mode={bulkDialog.mode}
                  selectedUsers={
                    bulkDialog.table === 'active'
                      ? selectedActiveUsers
                      : selectedInvitedUsers
                  }
                  onClose={this.closeBulk}
                  onComplete={this.completeBulk}
                />
              </PillErrorBoundary>
            )}

            <InviteUserMenu
              isOpen={isInviteMenuOpen}
              currentUser={user}
              rolesCatalogue={rolesCatalogue}
              closeMenu={() => this.setIsInviteMenuOpen(false)}
              onSendInviteFinished={() => {
                fetchAllUsers();
              }}
            />
          </Wrapper>
        )}
      </Layout>
    );
  }
}

export default SettingsUsers;
